<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { v4 as uuid } from 'uuid'
import { useRoute, useRouter } from 'vue-router/composables'
import { recommendCommonConfig } from './configs/common'
import { api } from '@/api'
import { useFinishedClick, useFinishedView } from '@/composables/tracks'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'
import { STORAGE_CUSTOM_THEME } from '@/constants'
import { brand } from '@/config/brands'

const props = defineProps<{
  contentId: string
}>()

// 读取章节阅读器的主题设置
const chapterTheme = useLocalStorage(STORAGE_CUSTOM_THEME, {
  background: '#F6F7F9',
  size: '38',
  skinId: '',
})

const config = recommendCommonConfig

const allData = ref<any>({})
const recommendations = ref<any>([])
const fetching = ref(false)
const error = ref<any>()
const pageSize = 10
const total = ref(0)

const route = useRoute()
const { finishedView } = useFinishedView()
const { finishedClick } = useFinishedClick()

const statusDisplay = computed(() => {
  return route.query.status === 'finish' ? config.texts.end : config.texts.ongoing
})

const statusDescription = computed(() => {
  return route.query.status === 'finish'
    ? config.texts.endDescription
    : config.texts.ongoingDescription
})

async function fetchRecommendations() {
  fetching.value = true
  try {
    const result = await api.get<any, GetContentGetfinishlikecolumnResponse>('/content/getFinishLikeColumn', {
      params: {
        contentId: props.contentId,
        pageIndex: 1,
        pageSize,
      },
    })

    const data = result.data
    allData.value = data
    recommendations.value = data?.resourceList?.records || []
    total.value = data?.resourceList?.total || 0

    // 曝光埋点
    finishedView({
      contentId: props.contentId,
      serialStatus: route.query.status === 'finish' ? 'completed' : 'ongoing',
      showNum: recommendations.value.length,
      title: 'finished',
    })
  }
  catch (_error) {
    error.value = _error
  }
  finally {
    fetching.value = false
  }
}

const router = useRouter()

function goToCollection() {
  // More 按钮点击埋点
  finishedClick({
    elementName: 'more',
    contentId: props.contentId,
    serialStatus: route.query.status === 'finish' ? 'completed' : 'ongoing',
    showNum: recommendations.value.length,
    title: 'finished',
  })

  router.push({
    name: 'collection',
    params: {
      columnId: props.contentId, // 完读推荐只需要contentId
    },
    query: {
      type: 'finish',
      status: route.query.status as string, // 传递 status 参数
    },
  })
}

function handleBookClick(book: any) {
  // 书籍点击埋点
  finishedClick({
    elementName: 'bookclick',
    contentId: props.contentId,
    serialStatus: route.query.status === 'finish' ? 'completed' : 'ongoing',
    showNum: recommendations.value.length,
    title: 'finished',
    contentIdRecbook: book.id,
  })

  // 导航到书籍详情页
  router.push({ name: READER_ROUTE_NAME, params: { contentId: book.id, chapterId: book.firstChapter!.id } })
}

onMounted(() => {
  fetchRecommendations()
})
</script>

<template>
  <div
    :class="[`brand-${brand}`, `h-100dvh overflow-scroll color-${chapterTheme.background.replace('#', '')}`]"
    :style="`background: ${chapterTheme.background}`"
  >
    <BasicNavbar />

    <div class="flex justify-center items-center mt-48px">
      <div class="w-30px h-1px theme-divider-bg" />
      <div class="mx-4 text-26px font-bold theme-text-primary">
        {{ statusDisplay }}
      </div>
      <div class="w-30px h-1px theme-divider-bg" />
    </div>
    <div class="text-center theme-text-secondary">
      {{ statusDescription }}
    </div>

    <div class="px-3.75 pb-5 pt-10 rounded">
      <div v-if="fetching" class="py-15px flex items-center justify-center space-x-4 text-13px theme-text-secondary">
        <div class="animate-spin">
          <i-icon-park-outline-loading />
        </div>
        <p>{{ config.texts.loading }}</p>
      </div>

      <div v-else-if="error" class="py-15px text-13px text-center theme-text-secondary">
        {{ config.texts.failedToLoad }}
      </div>

      <div v-else>
        <div v-if="recommendations && recommendations.length" class="flex items-baseline justify-between">
          <p class="text-17px font-bold theme-text-primary">
            {{ allData.showName || config.texts.youMightAlsoLike }}
          </p>
          <div v-if="total > pageSize" class="cursor-pointer" @click="goToCollection">
            <p class="text-13px flex items-center theme-text-secondary">
              <span>{{ config.texts.more }}</span>
              <i-icon-park-outline-right />
            </p>
          </div>
        </div>

        <div v-for="(item, index) in recommendations" :key="index">
          <div
            class="mt-8 flex items-center rounded-lg theme-card-bg px-2.5 py-3 relative cursor-pointer"
            @click="handleBookClick(item)"
          >
            <div class="w-108px absolute left-2.5 -top-4">
              <BizBookCover
                :id="uuid()"
                :src="item.contentCoverUrl || ''"
                :small-theater="!!item.small_theater"
                :extra="{
                  module_name: allData.showName,
                  module_id: allData.id,
                  module_type: allData.columnType,
                  author: item.authorInfo?.author,
                  ...item,
                }"
                class="rounded-lg"
              />
            </div>

            <div class="flex-1 overflow-hidden ml-120px">
              <p class="text-15px font-bold line-clamp-1 theme-text-primary">
                {{ item.contentName }}
              </p>
              <p class="text-13px mt-1 line-clamp-3 min-h-14 theme-text-content">
                {{ item.introduce }}
              </p>

              <div class="min-h-20px mt-10px">
                <BizShowTags v-if="item.tagList && item.tagList.length" :tags="item.tagList" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// 主题文字颜色 - 使用 CSS 变量
.theme-text-primary {
  color: var(--chapter-text-color, #32353A);
}

.theme-text-secondary {
  color: var(--chapter-text-color, #65686F);
  opacity: 0.7;
}

.theme-text-content {
  color: var(--chapter-text-color, #65686F);
  opacity: 0.85;
}

// 主题分隔线背景
.theme-divider-bg {
  background-color: var(--chapter-text-color, #32353A);
}

// 主题卡片背景
.theme-card-bg {
  background-color: var(--sub-btn-bg, #fff);
}
</style>
